import React, { useState, useEffect } from 'react';
import { XMarkIcon, EyeIcon, EyeSlashIcon, CheckCircleIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { TeamService } from '../../services/team.service';
import { toast } from 'react-hot-toast';
import { decryptData, decryptKeyData, encryptData } from '../../utils/encryption';
import { secureStore } from '../../utils/secureStore';

interface TransferableItem {
  id: string;
  name: string;
  type: 'password' | 'card' | 'bank' | 'personal' | 'category';
  shared?: boolean;
  shared_with?: string;
  team_id?: string;
  category_id?: string; // Added for linking passwords to categories
}

interface TransferItemsModalProps {
  isOpen: boolean;
  memberId: string;
  teamId?: string;
  onClose: () => void;
  onTransferComplete: (transferredCount: number) => void;
}

const TransferItemsModal: React.FC<TransferItemsModalProps> = ({
  isOpen,
  memberId,
  teamId,
  onClose,
  onTransferComplete
}) => {
  const [activeTab, setActiveTab] = useState<'passwords' | 'cards' | 'banks' | 'personal' | 'categories'>('categories');
  const [items, setItems] = useState<{
    passwords: TransferableItem[];
    cards: TransferableItem[];
    banks: TransferableItem[];
    personal: TransferableItem[];
    categories: TransferableItem[];
  }>({
    passwords: [],
    cards: [],
    banks: [],
    personal: [],
    categories: []
  });
  const [selectedItems, setSelectedItems] = useState<TransferableItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTransferring, setIsTransferring] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [visiblePasswords, setVisiblePasswords] = useState<string[]>([]);
  const [secretKey, setSecretKey] = useState<string>('');
  const [vaultKey, setVaultKey] = useState<string>('');

  useEffect(() => {
    if (isOpen && memberId) {
      fetchTransferableItems();
    }
  }, [isOpen, memberId, teamId]);

  const getMasterKey = () => {
    try {
      const vaultKey = secureStore.getVaultKey();
      setVaultKey(vaultKey);
    } catch (error) {
      throw new Error('Vault key not found. Please login again.');
    }
  }

  const fetchTransferableItems = async () => {
    try {
      setIsLoading(true);
      const response = await TeamService.getTransferableItems(memberId, teamId);
      console.log('response', response);
      setSecretKey(decryptKeyData(response.secretKey));
      await getMasterKey();
      console.log('secretKey', secretKey);
      if (response.success && response.items) {
        setItems(response.items);
      }
    } catch (error) {
      console.error('Error fetching transferable items:', error);
      toast.error('Failed to fetch transferable items');
    } finally {
      setIsLoading(false);
    }
  };

  // Decryption logic (keeping existing code)
  const decryptBufferData = (encryptedBuffer: any): string => {
    if (!encryptedBuffer) return '';

    try {
      if (encryptedBuffer.data && Array.isArray(encryptedBuffer.data)) {
        const uint8Array = new Uint8Array(encryptedBuffer.data);
        const encryptedString = new TextDecoder().decode(uint8Array);
        return decryptData(encryptedString, secretKey);
      }

      if (typeof encryptedBuffer === 'string') {
        return decryptData(encryptedBuffer, secretKey);
      }

      return '';
    } catch (error) {
      console.error('Error decrypting buffer:', error);
      return '';
    }
  };

  // New function specifically for card decryption
  const decryptCardBufferData = (encryptedBuffer: any): string => {
    if (!encryptedBuffer) return '';

    try {
      // Handle case where the field is already encrypted (has 'encrypted_' prefix)
      if (typeof encryptedBuffer === 'string' && encryptedBuffer.startsWith('encrypted_')) {
        return encryptedBuffer;
      }

      // Handle buffer data
      if (encryptedBuffer.data && Array.isArray(encryptedBuffer.data)) {
        const uint8Array = new Uint8Array(encryptedBuffer.data);
        const encryptedString = new TextDecoder().decode(uint8Array);
        return decryptData(encryptedString, secretKey);
      }

      // Handle direct string data
      if (typeof encryptedBuffer === 'string') {
        return decryptData(encryptedBuffer, secretKey);
      }

      return '';
    } catch (error) {
      console.error('Error decrypting card buffer:', error);
      return '';
    }
  };

  const reencryptPasswords = async (items: TransferableItem[]) => {
    // Decrypt passwords using the same pattern as VaultService
    const decryptedPasswords = items.map(item => {
      try {
        const typedItem = item as unknown as { id: string; username: string; password: string; notes: string };

        return {
          ...item,
          username: decryptBufferData(typedItem.username),
          password: decryptBufferData(typedItem.password),
          notes: decryptBufferData(typedItem.notes),
        };
      } catch (error) {
        console.error(`Failed to decrypt item ${item.id}:`, error);
        return item;
      }
    });

    console.log("Decrypted passwords:", decryptedPasswords);

    // Now encrypt the decrypted data using vault key (following VaultService pattern)
    const encryptedPasswords = decryptedPasswords.map(item => {
      const typedItem = item as unknown as { id: string; username: string; password: string; notes: string };
      try {
        if (!typedItem.username || !typedItem.password) {
          throw new Error('Missing required fields');
        }

        console.log("typedItem passwords", typedItem);

        let encryptedUsername, encryptedPassword, encryptedNotes;

        try {
          encryptedUsername = encryptData(typedItem.username, vaultKey);
          console.log("encryptedUsername", encryptedUsername);
        } catch (error) {
          console.error('Username encryption failed:', error);
          throw new Error('Failed to encrypt username');
        }

        try {
          encryptedPassword = encryptData(typedItem.password, vaultKey);
          console.log("encryptedPassword", encryptedPassword);
        } catch (error) {
          console.error('Password encryption failed:', error);
          throw new Error('Failed to encrypt password');
        }

        try {
          encryptedNotes = encryptData(typedItem.notes, vaultKey);
          console.log("encryptedNotes", encryptedNotes);
        } catch (error) {
          console.error('Notes encryption failed:', error);
          throw new Error('Failed to encrypt notes');
        }
        const { username, password, notes, ...rest } = item as unknown as { username: string; password: string; notes: string; id: string; name: string; type: "password" | "card" | "bank" | "personal" | "category"; shared?: boolean | undefined; shared_with?: string | undefined; team_id?: string | undefined; };

        return {
          ...rest,
          encrypted_username: encryptedUsername,
          encrypted_password: encryptedPassword,
          encrypted_notes: encryptedNotes,
        };
      } catch (error) {
        console.error(`Failed to encrypt item ${item.id}:`, error);
        return null;
      }
    }).filter(Boolean); // Remove any failed encryptions


    console.log("Encrypted passwords:", encryptedPasswords);

    return encryptedPasswords;
  }

  const reencryptCards = async (items: TransferableItem[]) => {
    // Decrypt cards using the card-specific pattern
    const decryptedCards = items.map(item => {
      try {
        const typedItem = item as unknown as { 
          id: string; 
          encrypted_card_holder_name?: string;
          encrypted_card_number?: string;
          encrypted_expiration_date?: string;
          encrypted_cvv?: string;
          encrypted_pin?: string;
          encrypted_postal_code?: string;
          encrypted_notes?: string;
          card_holder_name?: string;
          card_number?: string;
          expiration_date?: string;
          cvv?: string;
          pin?: string;
          postal_code?: string;
          notes?: string;
        };

        return {
          ...item,
          encrypted_card_holder_name: decryptCardBufferData(typedItem.encrypted_card_holder_name || typedItem.card_holder_name),
          encrypted_card_number: decryptCardBufferData(typedItem.encrypted_card_number || typedItem.card_number),
          encrypted_expiration_date: decryptCardBufferData(typedItem.encrypted_expiration_date || typedItem.expiration_date),
          encrypted_cvv: decryptCardBufferData(typedItem.encrypted_cvv || typedItem.cvv),
          encrypted_pin: decryptCardBufferData(typedItem.encrypted_pin || typedItem.pin),
          encrypted_postal_code: decryptCardBufferData(typedItem.encrypted_postal_code || typedItem.postal_code),
          encrypted_notes: decryptCardBufferData(typedItem.encrypted_notes || typedItem.notes),
        };
      } catch (error) {
        console.error(`Failed to decrypt item ${item.id}:`, error);
        return item;
      }
    });

    console.log("Decrypted cards:", decryptedCards);

    // Now encrypt the decrypted data using vault key
    const encryptedCards = decryptedCards.map(item => {
      const typedItem = item as unknown as { 
        id: string; 
        encrypted_card_holder_name: string;
        encrypted_card_number: string;
        encrypted_expiration_date: string;
        encrypted_cvv: string;
        encrypted_pin: string;
        encrypted_postal_code: string;
        encrypted_notes: string;
      };
      
      try {
        // Remove strict validation since fields may be optional
        let encryptedCardHolderName = '', encryptedCardNumber = '', encryptedExpirationDate = '', 
            encryptedCvv = '', encryptedPin = '', encryptedPostalCode = '', encryptedNotes = '';

        // Only encrypt fields that exist and aren't already encrypted
        if (typedItem.encrypted_card_holder_name && !typedItem.encrypted_card_holder_name.startsWith('encrypted_')) {
          try {
            encryptedCardHolderName = encryptData(typedItem.encrypted_card_holder_name, vaultKey);
          } catch (error) {
            console.error('Card holder name encryption failed:', error);
          }
        }

        if (typedItem.encrypted_card_number && !typedItem.encrypted_card_number.startsWith('encrypted_')) {
          try {
            encryptedCardNumber = encryptData(typedItem.encrypted_card_number, vaultKey);
          } catch (error) {
            console.error('Card number encryption failed:', error);
          }
        }

        if (typedItem.encrypted_expiration_date && !typedItem.encrypted_expiration_date.startsWith('encrypted_')) {
          try {
            encryptedExpirationDate = encryptData(typedItem.encrypted_expiration_date, vaultKey);
          } catch (error) {
            console.error('Expiration date encryption failed:', error);
          }
        }

        if (typedItem.encrypted_cvv && !typedItem.encrypted_cvv.startsWith('encrypted_')) {
          try {
            encryptedCvv = encryptData(typedItem.encrypted_cvv, vaultKey);
          } catch (error) {
            console.error('CVV encryption failed:', error);
          }
        }

        if (typedItem.encrypted_pin && !typedItem.encrypted_pin.startsWith('encrypted_')) {
          try {
            encryptedPin = encryptData(typedItem.encrypted_pin, vaultKey);
          } catch (error) {
            console.error('PIN encryption failed:', error);
          }
        }

        if (typedItem.encrypted_postal_code && !typedItem.encrypted_postal_code.startsWith('encrypted_')) {
          try {
            encryptedPostalCode = encryptData(typedItem.encrypted_postal_code, vaultKey);
          } catch (error) {
            console.error('Postal code encryption failed:', error);
          }
        }

        if (typedItem.encrypted_notes && !typedItem.encrypted_notes.startsWith('encrypted_')) {
          try {
            encryptedNotes = encryptData(typedItem.encrypted_notes, vaultKey);
          } catch (error) {
            console.error('Notes encryption failed:', error);
          }
        }

        const { encrypted_card_holder_name, encrypted_card_number, encrypted_expiration_date, encrypted_cvv, encrypted_pin, encrypted_postal_code, encrypted_notes, ...rest } = item as unknown as { encrypted_card_holder_name: string; encrypted_card_number: string; encrypted_expiration_date: string; encrypted_cvv: string; encrypted_pin: string; encrypted_postal_code: string; encrypted_notes: string; id: string; name: string; type: "password" | "card" | "bank" | "personal" | "category"; shared?: boolean | undefined; shared_with?: string | undefined; team_id?: string | undefined; };

        return {
          ...rest,
          encrypted_card_holder_name: encryptedCardHolderName || encrypted_card_holder_name,
          encrypted_card_number: encryptedCardNumber || encrypted_card_number,
          encrypted_expiration_date: encryptedExpirationDate || encrypted_expiration_date,
          encrypted_cvv: encryptedCvv || encrypted_cvv,
          encrypted_pin: encryptedPin || encrypted_pin,
          encrypted_postal_code: encryptedPostalCode || encrypted_postal_code,
          encrypted_notes: encryptedNotes || encrypted_notes,
        };
      } catch (error) {
        console.error(`Failed to encrypt item ${item.id}:`, error);
        return null;
      }
    }).filter(Boolean);

    console.log("Encrypted cards:", encryptedCards);
    return encryptedCards;
  }

  const reencryptBanks = async (items: TransferableItem[]) => {
    // Decrypt passwords using the same pattern as VaultService
    const decryptedBanks = items.map(item => {
      try {
        const typedItem = item as unknown as { id: string;  encrypted_routing_number: string; encrypted_account_number: string; encrypted_swift_code: string; encrypted_iban_number: string; encrypted_pin: string; encrypted_branch_address: string; encrypted_branch_phone: string; encrypted_notes: string };

        return {
          ...item,
          encrypted_routing_number: decryptCardBufferData(typedItem.encrypted_routing_number),
          encrypted_account_number: decryptCardBufferData(typedItem.encrypted_account_number),
          encrypted_swift_code: decryptCardBufferData(typedItem.encrypted_swift_code),
          encrypted_iban_number: decryptCardBufferData(typedItem.encrypted_iban_number),
          encrypted_pin: decryptCardBufferData(typedItem.encrypted_pin),
          encrypted_branch_address: decryptCardBufferData(typedItem.encrypted_branch_address),
          encrypted_branch_phone: decryptCardBufferData(typedItem.encrypted_branch_phone),
          encrypted_notes: decryptCardBufferData(typedItem.encrypted_notes),
        };
      } catch (error) {
        console.error(`Failed to decrypt item ${item.id}:`, error);
        return item;
      }
    });

    console.log("Decrypted banks:", decryptedBanks);

    // Now encrypt the decrypted data using vault key (following VaultService pattern)
    const encryptedBanks = decryptedBanks.map(item => {
      const typedItem = item as unknown as { id: string; encrypted_routing_number: string; encrypted_account_number: string; encrypted_swift_code: string; encrypted_iban_number: string; encrypted_pin: string; encrypted_branch_address: string; encrypted_branch_phone: string; encrypted_notes: string }; 
      try {
        if (!typedItem.encrypted_routing_number || !typedItem.encrypted_account_number) {
          throw new Error('Missing required fields');
        }

        let encryptedRoutingNumber, encryptedAccountNumber, encryptedSwiftCode, encryptedIbanNumber, encryptedPin, encryptedBranchAddress, encryptedBranchPhone, encryptedNotes;

        try {
          encryptedRoutingNumber = encryptData(typedItem.encrypted_routing_number, vaultKey);
        } catch (error) {
          console.error('Username encryption failed:', error);
          throw new Error('Failed to encrypt username');
        }

        try {
          encryptedAccountNumber = encryptData(typedItem.encrypted_account_number, vaultKey);
        } catch (error) {
          console.error('Account number encryption failed:', error);
          throw new Error('Failed to encrypt account number');
        }

        try {
          encryptedSwiftCode = encryptData(typedItem.encrypted_swift_code, vaultKey);
        } catch (error) {
          console.error('Swift code encryption failed:', error);
          throw new Error('Failed to encrypt swift code');
        }

        try {
          encryptedIbanNumber = encryptData(typedItem.encrypted_iban_number, vaultKey);
        } catch (error) {
          console.error('IBAN number encryption failed:', error);
          throw new Error('Failed to encrypt IBAN number');
        }

        try {
          encryptedPin = encryptData(typedItem.encrypted_pin, vaultKey);
        } catch (error) {
          console.error('PIN encryption failed:', error);
          throw new Error('Failed to encrypt PIN');
        }

        try {
          encryptedBranchAddress = encryptData(typedItem.encrypted_branch_address, vaultKey);
        } catch (error) {
          console.error('Branch address encryption failed:', error);
          throw new Error('Failed to encrypt branch address');
        }
        try {
          encryptedBranchPhone = encryptData(typedItem.encrypted_branch_phone, vaultKey);
        } catch (error) {
          console.error('Branch phone encryption failed:', error);
          throw new Error('Failed to encrypt branch phone');
        }
        try {
          encryptedNotes = encryptData(typedItem.encrypted_notes, vaultKey);
        } catch (error) {
          console.error('Notes encryption failed:', error);
          throw new Error('Failed to encrypt notes');
        }
        const { encrypted_routing_number, encrypted_account_number, encrypted_swift_code, encrypted_iban_number, encrypted_pin, encrypted_branch_address, encrypted_branch_phone, encrypted_notes, ...rest } = item as unknown as { encrypted_routing_number: string; encrypted_account_number: string; encrypted_swift_code: string; encrypted_iban_number: string; encrypted_pin: string; encrypted_branch_address: string; encrypted_branch_phone: string; encrypted_notes: string; id: string; name: string; type: "password" | "card" | "bank" | "personal" | "category"; shared?: boolean | undefined; shared_with?: string | undefined; team_id?: string | undefined; };

        return {
          ...rest,
          encrypted_routing_number: encryptedRoutingNumber,
          encrypted_account_number: encryptedAccountNumber,
          encrypted_swift_code: encryptedSwiftCode,
          encrypted_iban_number: encryptedIbanNumber,
          encrypted_pin: encryptedPin,
          encrypted_branch_address: encryptedBranchAddress,
          encrypted_branch_phone: encryptedBranchPhone,
          encrypted_notes: encryptedNotes,
        };
      } catch (error) {
        console.error(`Failed to encrypt item ${item.id}:`, error);
        return null;
      }
    }).filter(Boolean); // Remove any failed encryptions


    console.log("Encrypted banks:", encryptedBanks);

    return encryptedBanks;

  }


  const reencryptPersonal = async (items: TransferableItem[]) => {
    // Decrypt passwords using the same pattern as VaultService
    const decryptedPersonal = items.map(item => {
      try {
        const typedItem = item as unknown as { id: string; encrypted_first_name: string; encrypted_middle_name: string; encrypted_last_name: string; encrypted_username: string; encrypted_email_address: string; encrypted_phone: string; encrypted_address_1: string; encrypted_address_2: string; encrypted_city_town: string; encrypted_state: string; encrypted_zip_postal_code: string; encrypted_country: string; encrypted_timezone: string; encrypted_notes: string };

        return {
          ...item,
          encrypted_first_name: decryptCardBufferData(typedItem.encrypted_first_name),
          encrypted_middle_name: decryptCardBufferData(typedItem.encrypted_middle_name),
          encrypted_last_name: decryptCardBufferData(typedItem.encrypted_last_name),
          encrypted_username: decryptCardBufferData(typedItem.encrypted_username),
          encrypted_email_address: decryptCardBufferData(typedItem.encrypted_email_address),
          encrypted_phone: decryptCardBufferData(typedItem.encrypted_phone),
          encrypted_address_1: decryptCardBufferData(typedItem.encrypted_address_1),
          encrypted_address_2: decryptCardBufferData(typedItem.encrypted_address_2),
          encrypted_city_town: decryptCardBufferData(typedItem.encrypted_city_town),
          encrypted_state: decryptCardBufferData(typedItem.encrypted_state),
          encrypted_zip_postal_code: decryptCardBufferData(typedItem.encrypted_zip_postal_code),
          encrypted_country: decryptCardBufferData(typedItem.encrypted_country),
          encrypted_timezone: decryptCardBufferData(typedItem.encrypted_timezone),
          encrypted_notes: decryptCardBufferData(typedItem.encrypted_notes),
        };
      } catch (error) {
        console.error(`Failed to decrypt item ${item.id}:`, error);
        return item;
      }
    });

    console.log("Decrypted personal:", decryptedPersonal);

    // Now encrypt the decrypted data using vault key (following VaultService pattern)
    const encryptedPersonal = decryptedPersonal.map(item => {
      const typedItem = item as unknown as { id: string; encrypted_first_name: string; encrypted_middle_name: string; encrypted_last_name: string; encrypted_username: string; encrypted_email_address: string; encrypted_phone: string; encrypted_address_1: string; encrypted_address_2: string; encrypted_city_town: string; encrypted_state: string; encrypted_zip_postal_code: string; encrypted_country: string; encrypted_timezone: string; encrypted_notes: string };
      try {
        if (!typedItem.encrypted_first_name || !typedItem.encrypted_last_name) {
          throw new Error('Missing required fields');
        }

        let encryptedFirstName, encryptedMiddleName, encryptedLastName, encryptedUsername, encryptedEmailAddress, encryptedPhone, encryptedAddress1, encryptedAddress2, encryptedCityTown, encryptedState, encryptedZipPostalCode, encryptedCountry, encryptedTimezone, encryptedNotes;

        try {
          encryptedFirstName = encryptData(typedItem.encrypted_first_name, vaultKey);
        } catch (error) {
          console.error('Username encryption failed:', error);
          throw new Error('Failed to encrypt username');
        }

        try {
          encryptedMiddleName = encryptData(typedItem.encrypted_middle_name, vaultKey);
        } catch (error) {
          console.error('Middle name encryption failed:', error);
          throw new Error('Failed to encrypt middle name');
        }

        try {
          encryptedLastName = encryptData(typedItem.encrypted_last_name, vaultKey);
        } catch (error) {
          console.error('Last name encryption failed:', error);
          throw new Error('Failed to encrypt last name');
        }

        try {
          encryptedUsername = encryptData(typedItem.encrypted_username, vaultKey);
        } catch (error) {
          console.error('Username encryption failed:', error);
          throw new Error('Failed to encrypt username');
        }

        try {
          encryptedEmailAddress = encryptData(typedItem.encrypted_email_address, vaultKey);
        } catch (error) {
          console.error('Email address encryption failed:', error);
          throw new Error('Failed to encrypt email address');
        }

        try {
          encryptedPhone = encryptData(typedItem.encrypted_phone, vaultKey);
        } catch (error) {
          console.error('Phone encryption failed:', error);
          throw new Error('Failed to encrypt phone');
        }

        try {
          encryptedAddress1 = encryptData(typedItem.encrypted_address_1, vaultKey);
        } catch (error) {
          console.error('Address 1 encryption failed:', error);
          throw new Error('Failed to encrypt address 1');
        }
        try {
          encryptedAddress2 = encryptData(typedItem.encrypted_address_2, vaultKey);
        } catch (error) {
          console.error('Address 2 encryption failed:', error);
          throw new Error('Failed to encrypt address 2');
        }
        try {
          encryptedCityTown = encryptData(typedItem.encrypted_city_town, vaultKey);
        } catch (error) {
          console.error('City/town encryption failed:', error);
          throw new Error('Failed to encrypt city/town');
        }
        try {
          encryptedState = encryptData(typedItem.encrypted_state, vaultKey);
        } catch (error) {
          console.error('State encryption failed:', error);
          throw new Error('Failed to encrypt state');
        }
        try {
          encryptedZipPostalCode = encryptData(typedItem.encrypted_zip_postal_code, vaultKey);
        } catch (error) {
          console.error('Zip/postal code encryption failed:', error);
          throw new Error('Failed to encrypt zip/postal code');
        }
        try {
          encryptedCountry = encryptData(typedItem.encrypted_country, vaultKey);
        } catch (error) {
          console.error('Country encryption failed:', error);
          throw new Error('Failed to encrypt country');
        }
        try {
          encryptedTimezone = encryptData(typedItem.encrypted_timezone, vaultKey);
        } catch (error) {
          console.error('Timezone encryption failed:', error);
          throw new Error('Failed to encrypt timezone');
        }
        try {
          encryptedNotes = encryptData(typedItem.encrypted_notes, vaultKey);
        } catch (error) {
          console.error('Notes encryption failed:', error);
          throw new Error('Failed to encrypt notes');
        }
        const { encrypted_first_name, encrypted_middle_name, encrypted_last_name, encrypted_username, encrypted_email_address, encrypted_phone, encrypted_address_1, encrypted_address_2, encrypted_city_town, encrypted_state, encrypted_zip_postal_code, encrypted_country, encrypted_timezone, encrypted_notes, ...rest } = item as unknown as { encrypted_first_name: string; encrypted_middle_name: string; encrypted_last_name: string; encrypted_username: string; encrypted_email_address: string; encrypted_phone: string; encrypted_address_1: string; encrypted_address_2: string; encrypted_city_town: string; encrypted_state: string; encrypted_zip_postal_code: string; encrypted_country: string; encrypted_timezone: string; encrypted_notes: string; id: string; name: string; type: "password" | "card" | "bank" | "personal" | "category"; shared?: boolean | undefined; shared_with?: string | undefined; team_id?: string | undefined; };

        return {
          ...rest,
          encrypted_first_name: encryptedFirstName,
          encrypted_middle_name: encryptedMiddleName,
          encrypted_last_name: encryptedLastName,
          encrypted_username: encryptedUsername,
          encrypted_email_address: encryptedEmailAddress,
          encrypted_phone: encryptedPhone,
          encrypted_address_1: encryptedAddress1,
          encrypted_address_2: encryptedAddress2,
          encrypted_city_town: encryptedCityTown,
          encrypted_state: encryptedState,
          encrypted_zip_postal_code: encryptedZipPostalCode,
          encrypted_country: encryptedCountry,
          encrypted_timezone: encryptedTimezone,
          encrypted_notes: encryptedNotes,
        };
      } catch (error) {
        console.error(`Failed to encrypt item ${item.id}:`, error);
        return null;
      }
    }).filter(Boolean); // Remove any failed encryptions


    console.log("Encrypted personal:", encryptedPersonal);

    return encryptedPersonal;

  }

  const handleTransfer = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item to transfer');
      return;
    }

    try {
      console.log("selectedItems", selectedItems);

      const encryptedPasswords = await reencryptPasswords(selectedItems.map(item => item.type === 'password' ? item : null).filter(Boolean) as TransferableItem[]);

      const selectedCategories = selectedItems.filter(item => item.type === 'category');

      const encryptedCards = await reencryptCards(selectedItems.map(item => item.type === 'card' ? item : null).filter(Boolean) as TransferableItem[]);

      const encryptedBanks = await reencryptBanks(selectedItems.map(item => item.type === 'bank' ? item : null).filter(Boolean) as TransferableItem[]);

      const encryptedPersonal = await reencryptPersonal(selectedItems.map(item => item.type === 'personal' ? item : null).filter(Boolean) as TransferableItem[]);


      const allSelectedItems: (TransferableItem | string)[] = [
        ...(encryptedPasswords as unknown as TransferableItem[] || []),
        ...(encryptedCards as unknown as TransferableItem[] || []), // cards
        ...(encryptedBanks as unknown as TransferableItem[] || []), // banks
        ...(encryptedPersonal as unknown as TransferableItem[] || []), // personal
        ...selectedCategories, // categories
      ];
      console.log("allSelectedItems", allSelectedItems);

      setIsTransferring(true);
      const response = await TeamService.transferSelectedItems(memberId, allSelectedItems);
      if (response.success) {
        toast.success(`Successfully transferred ${response.transferredCount} items`);
        onTransferComplete(response.transferredCount);
      }
      else {
        setIsTransferring(false);
        toast.error('Failed to transfer items');
      }
    } catch (error) {
      console.error('Error transferring items:', error);
      toast.error('Failed to transfer items');
    } finally {
      setIsTransferring(false);
    }
  };

  const toggleItemSelection = (item: TransferableItem) => {
    if (item.type === 'category') {
      // If selecting/deselecting a vault
      const isCurrentlySelected = selectedItems.some(selected => selected.id === item.id && selected.type === item.type);

      if (isCurrentlySelected) {
        // Remove the vault and its associated items
        setSelectedItems(selectedItems.filter(selected =>
          !(selected.id === item.id && selected.type === item.type) && // Remove vault
          !(selected.type === 'password' && items.passwords.find(p => p.category_id === item.id)) && // Remove passwords
          !(selected.type === 'card' && items.cards.find(c => c.category_id === item.id)) && // Remove cards
          !(selected.type === 'bank' && items.banks.find(b => b.category_id === item.id)) && // Remove banks
          !(selected.type === 'personal' && items.personal.find(p => p.category_id === item.id)) // Remove personal
        ));
      } else {
        // Add the vault and its associated items
        const associatedPasswords = items.passwords.filter(password => password.category_id === item.id);
        const associatedCards = items.cards.filter(card => card.category_id === item.id);
        const associatedBanks = items.banks.filter(bank => bank.category_id === item.id);
        const associatedPersonal = items.personal.filter(personal => personal.category_id === item.id);

        const newItems = [
          ...selectedItems,
          item,
          ...associatedPasswords.filter(password =>
            !selectedItems.some(selected => selected.id === password.id && selected.type === 'password')
          ),
          ...associatedCards.filter(card =>
            !selectedItems.some(selected => selected.id === card.id && selected.type === 'card')
          ),
          ...associatedBanks.filter(bank =>
            !selectedItems.some(selected => selected.id === bank.id && selected.type === 'bank')
          ),
          ...associatedPersonal.filter(personal =>
            !selectedItems.some(selected => selected.id === personal.id && selected.type === 'personal')
          )
        ];
        setSelectedItems(newItems);
      }
    } else {
      // For non-vault items, keep the original behavior
      if (selectedItems.some(selected => selected.id === item.id && selected.type === item.type)) {
        setSelectedItems(selectedItems.filter(selected => !(selected.id === item.id && selected.type === item.type)));
      } else {
        setSelectedItems([...selectedItems, item]);
      }
    }
  };

  const selectAllInTab = (checked: boolean) => {
    if (checked) {
      if (activeTab === 'categories') {
        // When selecting all vaults, also select all their associated items
        const allVaults = items.categories;
        const allVaultIds = allVaults.map(vault => vault.id);

        const allAssociatedPasswords = items.passwords.filter(password =>
          allVaultIds.includes(password.category_id as string)
        );
        const allAssociatedCards = items.cards.filter(card =>
          allVaultIds.includes(card.category_id as string)
        );
        const allAssociatedBanks = items.banks.filter(bank =>
          allVaultIds.includes(bank.category_id as string)
        );
        const allAssociatedPersonal = items.personal.filter(personal =>
          allVaultIds.includes(personal.category_id as string)
        );

        const itemsToAdd = [
          ...allVaults.filter(vault =>
            !selectedItems.some(selected => selected.id === vault.id && selected.type === vault.type)
          ),
          ...allAssociatedPasswords.filter(password =>
            !selectedItems.some(selected => selected.id === password.id && selected.type === password.type)
          ),
          ...allAssociatedCards.filter(card =>
            !selectedItems.some(selected => selected.id === card.id && selected.type === card.type)
          ),
          ...allAssociatedBanks.filter(bank =>
            !selectedItems.some(selected => selected.id === bank.id && selected.type === bank.type)
          ),
          ...allAssociatedPersonal.filter(personal =>
            !selectedItems.some(selected => selected.id === personal.id && selected.type === personal.type)
          )
        ];
        setSelectedItems([...selectedItems, ...itemsToAdd]);
      } else {
        // For other tabs, keep original behavior
        const itemsToAdd = items[activeTab].filter(
          item => !selectedItems.some(selected => selected.id === item.id && selected.type === item.type)
        );
        setSelectedItems([...selectedItems, ...itemsToAdd]);
      }
    } else {
      if (activeTab === 'categories') {
        // When deselecting all vaults, also deselect all their associated items
        const allVaultIds = items.categories.map(vault => vault.id);
        setSelectedItems(selectedItems.filter(selected =>
          !(items.categories.some(vault => vault.id === selected.id)) && // Remove vaults
          !(selected.type === 'password' && items.passwords.find(p => allVaultIds.includes(p.category_id as string))) && // Remove passwords
          !(selected.type === 'card' && items.cards.find(c => allVaultIds.includes(c.category_id as string))) && // Remove cards
          !(selected.type === 'bank' && items.banks.find(b => allVaultIds.includes(b.category_id as string))) && // Remove banks
          !(selected.type === 'personal' && items.personal.find(p => allVaultIds.includes(p.category_id as string))) // Remove personal
        ));
      } else {
        // For other tabs, keep original behavior
        setSelectedItems(selectedItems.filter(
          selected => !items[activeTab].some(item => item.id === selected.id && item.type === selected.type)
        ));
      }
    }
  };

  const togglePasswordVisibility = (itemId: string) => {
    if (visiblePasswords.includes(itemId)) {
      setVisiblePasswords(visiblePasswords.filter(id => id !== itemId));
    } else {
      setVisiblePasswords([...visiblePasswords, itemId]);
    }
  };

  const filteredItems = items[activeTab].filter(item =>
    item.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isAllSelected = filteredItems.length > 0 &&
    filteredItems.every(item =>
      selectedItems.some(selected => selected.id === item.id && selected.type === item.type)
    );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg leading-6 font-medium text-gray-900">
                Transfer Items
              </h3>
              <button
                className="text-gray-400 hover:text-gray-500"
                onClick={onClose}
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            ) : (
              <div>
                {/* Tab navigation */}
                <div className="border-b border-gray-200 mb-4">
                  <nav className="flex -mb-px">
                    <button
                      className={`py-2 px-4 text-sm font-medium ${activeTab === 'categories'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      onClick={() => setActiveTab('categories')}
                    >
                      Vaults ({items.categories.length})
                    </button>
                    <button
                      className={`py-2 px-4 text-sm font-medium ${activeTab === 'passwords'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      onClick={() => setActiveTab('passwords')}
                    >
                      Passwords ({items.passwords.length})
                    </button>
                    <button
                      className={`py-2 px-4 text-sm font-medium ${activeTab === 'cards'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      onClick={() => setActiveTab('cards')}
                    >
                      Cards ({items.cards.length})
                    </button>
                    <button
                      className={`py-2 px-4 text-sm font-medium ${activeTab === 'banks'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      onClick={() => setActiveTab('banks')}
                    >
                      Bank Accounts ({items.banks.length})
                    </button>
                    <button
                      className={`py-2 px-4 text-sm font-medium ${activeTab === 'personal'
                        ? 'border-b-2 border-blue-500 text-blue-600'
                        : 'text-gray-500 hover:text-gray-700'
                        }`}
                      onClick={() => setActiveTab('personal')}
                    >
                      Personal Info ({items.personal.length})
                    </button>

                  </nav>
                </div>

                {/* Search and Select All */}
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={(e) => selectAllInTab(e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Select All</span>
                  </div>
                  <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm"
                  />
                </div>

                {/* Item List */}
                <div className="max-h-96 overflow-y-auto">
                  {filteredItems.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      No {activeTab} available to transfer
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {filteredItems.map((item) => (
                        <div
                          key={`${item.type}-${item.id}`}
                          className={`p-3 border rounded-md ${selectedItems.some(selected => selected.id === item.id && selected.type === item.type)
                            ? 'bg-blue-50 border-blue-200'
                            : 'bg-white border-gray-200'
                            } hover:bg-gray-50 transition-colors`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              <input
                                type="checkbox"
                                checked={selectedItems.some(selected => selected.id === item.id && selected.type === item.type)}
                                onChange={() => toggleItemSelection(item)}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <div className="ml-3">
                                <p className="text-sm font-medium text-gray-900">{item.name || 'Unnamed Item'}</p>
                                <div className="flex items-center mt-1">
                                  {item.shared && (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      Shared
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>


                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Info and Action Buttons */}
                <div className="mt-6">
                  {activeTab === 'categories' && (
                    <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md flex items-center text-sm">
                      <InformationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                      <span>
                        Note: Once you select a vault, all items belongs to that vault will be automatically selected.
                      </span>
                    </div>
                  )}
                  <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md flex items-center text-sm">
                    <InformationCircleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                    <span>
                      Selected items will be transferred to your vault and will be deleted from the team.
                      This action cannot be undone.
                    </span>
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      className="py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      onClick={onClose}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`py-2 px-4 rounded-md shadow-sm text-sm font-medium text-white ${selectedItems.length === 0 || isTransferring
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                      onClick={handleTransfer}
                      disabled={selectedItems.length === 0 || isTransferring}
                    >
                      {isTransferring ? (
                        <div className="flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                          Transferring...
                        </div>
                      ) : (
                        `Transfer Selected (${selectedItems.length})`
                      )}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TransferItemsModal;
